import { connectionKindOf } from '../../types/network';
import React, { useState, useMemo } from 'react';
import { Hausanschluss, TechnischeRessource } from '../../types/network';
import { useScenarioStore } from '../../store/scenarioStore';
import { useStressTestStore } from '../../store/stressTestStore';
import { useViewMode } from '../../context/useViewMode';
import { detailStyles, headerColors } from '../../styles/detailStyles';
import { puToVolts, PU_BASE_LN_V, puDeviation, puDeviationPct } from '../../utils/electricalConstants';
import { fmt, fmtOpt } from '../../utils/numberFormat';

interface ScenarioEditSidebarProps {
  elementId: string;
  onClose?: () => void;
}

/**
 * ScenarioEditSidebar - Edit panel for modifying network elements within a scenario.
 * Changes are saved to the scenarioStore (not the live network).
 * Styled to match HausanschlussDetails.
 */
export const ScenarioEditSidebar: React.FC<ScenarioEditSidebarProps> = ({ elementId }) => {
  const viewMode = useViewMode();
  const isStressTest = viewMode === 'stress-test';
  const stressElements = useStressTestStore(state => state.stressElements);
  const {
    updateElementOverride,
    removeElementOverride,
    addTechnischeRessource,
    removeTechnischeRessource,
    hasOverride,
    getElementOverride,
  } = useScenarioStore();

  // Find the original element from the stress test elements (isolated from main map zone)
  const originalElement = useMemo(() => {
    return stressElements.find(e => e.id === elementId) as Hausanschluss | undefined;
  }, [stressElements, elementId]);

  // Get current override values
  const override = getElementOverride(elementId);

  // Get stress test results for this element
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  const stressData = stressTestResults?.results?.[elementId];

  // Every device takes part in a planning run: existing ones through the
  // observed frame, added/changed ones through the rated delta.
  const isResourceActive = (_type: string): boolean => true;

  // State for new resource forms
  const [showAddPv, setShowAddPv] = useState(false);
  const [newPvPower, setNewPvPower] = useState(10);
  const [showAddWallbox, setShowAddWallbox] = useState(false);
  const [newWallboxPower, setNewWallboxPower] = useState(11);
  const [showAddWaermepumpe, setShowAddWaermepumpe] = useState(false);
  const [newWaermepumpePower, setNewWaermepumpePower] = useState(5);


  // Calculate merged technische Ressourcen (must be before early return)
  const mergedResources = useMemo(() => {
    if (!originalElement) return [];
    let resources = [...originalElement.technischeRessourcen];

    // Remove resources marked for removal
    if (override?.removedResourceIds?.length) {
      resources = resources.filter(r => !override.removedResourceIds!.includes(r.id));
    }

    // Add new resources
    if (override?.addedResources?.length) {
      resources = [...resources, ...override.addedResources];
    }

    return resources;
  }, [originalElement, override]);

  if (!originalElement) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-primary-400">
        Element nicht gefunden
      </div>
    );
  }

  // Calculate merged values (original + override)
  const mergedJahresverbrauch = override?.jahresverbrauch ?? originalElement.annualConsumption;
  const mergedConnectionPower = override?.connectionPower ?? originalElement.connectionPower;
  const mergedLoadProfile = override?.loadProfile ?? originalElement.loadProfile;

  // Check which fields have changes
  const hasJahresverbrauchChange = override?.jahresverbrauch !== undefined && override.jahresverbrauch !== originalElement.annualConsumption;
  const hasConnectionPowerChange = override?.connectionPower !== undefined && override.connectionPower !== originalElement.connectionPower;
  const hasBaseLoadChange = override?.baseLoadKw !== undefined;
  const hasResourceChanges = (override?.addedResources?.length || 0) > 0 || (override?.removedResourceIds?.length || 0) > 0;
  const hasAnyChanges = hasOverride(elementId);

  // Check which resource types already exist
  const hasPvAnlage = mergedResources.some(r => r.type === 'pv_anlage');
  const hasWallbox = mergedResources.some(r => r.type === 'wallbox');
  const hasHeatPump = mergedResources.some(r => r.type === 'heat_pump');

  const handleJahresverbrauchChange = (value: number) => {
    if (value === originalElement.annualConsumption) {
      const newOverride = { ...override };
      delete newOverride.jahresverbrauch;
      if (Object.keys(newOverride).length === 0 ||
          (Object.keys(newOverride).length === 1 && newOverride.addedResources?.length === 0)) {
        removeElementOverride(elementId);
      } else {
        updateElementOverride(elementId, newOverride);
      }
    } else {
      updateElementOverride(elementId, { jahresverbrauch: value });
    }
  };

  const handleConnectionPowerChange = (value: number) => {
    if (value === originalElement.connectionPower) {
      const newOverride = { ...override };
      delete newOverride.connectionPower;
      if (Object.keys(newOverride).length === 0) {
        removeElementOverride(elementId);
      } else {
        updateElementOverride(elementId, newOverride);
      }
    } else {
      updateElementOverride(elementId, { connectionPower: value });
    }
  };

  const handleLoadProfileChange = (value: string) => {
    if (value === originalElement.loadProfile) {
      const newOverride = { ...override };
      delete newOverride.loadProfile;
      if (Object.keys(newOverride).length === 0) {
        removeElementOverride(elementId);
      } else {
        updateElementOverride(elementId, newOverride);
      }
    } else {
      updateElementOverride(elementId, { loadProfile: value });
    }
  };

  const handleBaseLoadChange = (value: number | null) => {
    if (value === null) {
      // Remove the baseLoadKw override
      const newOverride = { ...override };
      delete newOverride.baseLoadKw;
      if (Object.keys(newOverride).length === 0) {
        removeElementOverride(elementId);
      } else {
        updateElementOverride(elementId, newOverride);
      }
    } else {
      updateElementOverride(elementId, { baseLoadKw: value });
    }
  };

  const handleAddPvAnlage = () => {
    const newResource: TechnischeRessource = {
      id: `scenario-pv-${Date.now()}`,
      type: 'pv_anlage',
      maxPower: newPvPower,
    };
    addTechnischeRessource(elementId, newResource);
    setShowAddPv(false);
  };

  const handleAddWallbox = () => {
    const newResource: TechnischeRessource = {
      id: `scenario-wallbox-${Date.now()}`,
      type: 'wallbox',
      maxPower: newWallboxPower,
    };
    addTechnischeRessource(elementId, newResource);
    setShowAddWallbox(false);
  };

  const handleAddWaermepumpe = () => {
    const newResource: TechnischeRessource = {
      id: `scenario-wp-${Date.now()}`,
      type: 'heat_pump',
      maxPower: newWaermepumpePower,
    };
    addTechnischeRessource(elementId, newResource);
    setShowAddWaermepumpe(false);
  };

  const handleRemoveResource = (resourceId: string) => {
    const isAddedResource = override?.addedResources?.some(r => r.id === resourceId);
    if (isAddedResource) {
      const newAddedResources = override?.addedResources?.filter(r => r.id !== resourceId) || [];
      updateElementOverride(elementId, { addedResources: newAddedResources });
    } else {
      removeTechnischeRessource(elementId, resourceId);
    }
  };

  // Get the effective power for a resource (original + override)
  const getResourcePower = (resource: TechnischeRessource): number => {
    const resourceOverride = override?.modifiedResources?.[resource.id];
    return resourceOverride?.maxPower ?? resource.maxPower;
  };

  // Check if a resource has been modified
  const isResourceModified = (resourceId: string): boolean => {
    return override?.modifiedResources?.[resourceId]?.maxPower !== undefined;
  };

  // Update resource power in scenario (NOT in live network!)
  const handleResourcePowerChange = (resourceId: string, newPower: number) => {
    const isAddedResource = override?.addedResources?.some(r => r.id === resourceId);

    if (isAddedResource) {
      // For added resources, update directly in addedResources array
      const newAddedResources = override?.addedResources?.map(r =>
        r.id === resourceId ? { ...r, maxPower: newPower } : r
      ) || [];
      updateElementOverride(elementId, { addedResources: newAddedResources });
    } else {
      // For original resources, use modifiedResources
      const currentModified = override?.modifiedResources || {};
      const originalResource = originalElement.technischeRessourcen.find(r => r.id === resourceId);

      if (originalResource && newPower === originalResource.maxPower) {
        // Remove override if value equals original
        const restModified = Object.fromEntries(
          Object.entries(currentModified).filter(([key]) => key !== resourceId)
        );
        updateElementOverride(elementId, {
          modifiedResources: Object.keys(restModified).length > 0 ? restModified : undefined
        });
      } else {
        // Add/update override
        updateElementOverride(elementId, {
          modifiedResources: {
            ...currentModified,
            [resourceId]: { maxPower: newPower }
          }
        });
      }
    }
  };

  const handleResetAllChanges = () => {
    removeElementOverride(elementId);
  };

  // Calculate voltage for stress test display — use worst (min) phase, not average
  const stressPhaseVoltages = [stressData?.voltageL1Pu, stressData?.voltageL2Pu, stressData?.voltageL3Pu]
    .filter((v): v is number => v != null);
  let voltageLnPu: number | undefined;
  let voltageLnV: number | undefined;
  if (stressPhaseVoltages.length > 0) {
    voltageLnPu = Math.min(...stressPhaseVoltages);
    voltageLnV = puToVolts(voltageLnPu);
  } else if (stressData?.voltageKv !== undefined) {
    // Fallback to average if per-phase data not available
    voltageLnV = (stressData.voltageKv * 1000) / Math.sqrt(3);
    voltageLnPu = voltageLnV / PU_BASE_LN_V;
  }

  return (
    <div className={`flex flex-col ${detailStyles.layout.section}`}>
      {/* Header - same style as HausanschlussDetails */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.hausanschluss.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.hausanschluss.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Hausanschluss</h2>
              <p className={detailStyles.header.subtitle}>{originalElement.name}</p>
            </div>
          </div>
          {/* Scenario mode indicator */}
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-orange-500/80 text-white text-xs font-medium rounded">
              Szenario
            </span>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.hausanschluss.id}`}>ID: {originalElement.id}</div>
      </div>

      {/* Info Banner - Scenario warning */}
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md px-3 py-2">
        <p className="text-xs text-orange-800 dark:text-orange-200">
          Änderungen werden nur im Szenario gespeichert.
        </p>
      </div>

      {/* Jahresverbrauch, Anschlussleistung, Lastprofil - hidden in stress test (controlled by Grundlast parameter) */}
      {!isStressTest && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className={`bg-gray-50 dark:bg-primary-700 px-2 py-1.5 rounded ${hasJahresverbrauchChange ? 'ring-2 ring-orange-400' : ''}`}>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                Jahresverbrauch
                {hasJahresverbrauchChange && <span className="text-orange-500 ml-1">*</span>}
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={mergedJahresverbrauch}
                  onChange={(e) => handleJahresverbrauchChange(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500"
                />
                <span className="text-sm text-gray-500 dark:text-primary-400">kWh</span>
              </div>
            </div>
            <div className={`bg-gray-50 dark:bg-primary-700 px-2 py-1.5 rounded ${hasConnectionPowerChange ? 'ring-2 ring-orange-400' : ''}`}>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                Anschlussleistung
                {hasConnectionPowerChange && <span className="text-orange-500 ml-1">*</span>}
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={mergedConnectionPower}
                  onChange={(e) => handleConnectionPowerChange(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500"
                />
                <span className="text-sm text-gray-500 dark:text-primary-400">kW</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded">
            <div className="text-xs text-gray-600 dark:text-gray-300 mb-1.5">Lastprofil</div>
            <select
              value={mergedLoadProfile}
              onChange={(e) => handleLoadProfileChange(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500"
            >
              <option value="H0">H0 - Haushalt</option>
              <option value="G0">G0 - Gewerbe allgemein</option>
              <option value="G1">G1 - Gewerbe werktags 8-18 Uhr</option>
              <option value="G2">G2 - Gewerbe mit Verbrauch in Abendstunden</option>
              <option value="G3">G3 - Gewerbe durchlaufend</option>
              <option value="G4">G4 - Laden/Friseur</option>
              <option value="G5">G5 - Bäckerei mit Backstube</option>
              <option value="G6">G6 - Wochenendbetrieb</option>
              <option value="L0">L0 - Landwirtschaft allgemein</option>
              <option value="L1">L1 - Landwirtschaft mit Milchwirtschaft</option>
              <option value="L2">L2 - Übrige Landwirtschaft</option>
            </select>
          </div>
        </>
      )}

      {/* Direkte Haushaltslast - Override for stress test */}
      <div className={`bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded ${hasBaseLoadChange ? 'ring-2 ring-orange-400' : ''}`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {originalElement && connectionKindOf(originalElement) !== 'household' && connectionKindOf(originalElement) !== 'mixed' ? 'Grundlast (direkt)' : 'Haushaltslast (direkt)'}
            {hasBaseLoadChange && <span className="text-orange-500 ml-1">*</span>}
          </div>
          {hasBaseLoadChange && (
            <button
              onClick={() => handleBaseLoadChange(null)}
              className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
            >
              Zurücksetzen
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={override?.baseLoadKw ?? ''}
            placeholder="Automatisch"
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                handleBaseLoadChange(null);
              } else {
                handleBaseLoadChange(Number(val));
              }
            }}
            min={0}
            step={0.1}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500 placeholder:text-gray-400 dark:placeholder:text-primary-500"
          />
          <span className="text-sm text-gray-500 dark:text-primary-400">kW</span>
        </div>
        <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1">
          {hasBaseLoadChange
            ? 'Überschreibt Jahresverbrauch/Lastprofil-Berechnung'
            : 'Leer = automatisch aus Jahresverbrauch + Lastprofil'
          }
        </p>
      </div>

      {/* Stresstest-Ergebnisse Section - styled like Voltage section in HausanschlussDetails */}
      {stressTestResults && (
        <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
          <div className="bg-linear-to-r from-orange-50 to-white dark:from-orange-900/20 dark:to-primary-800 px-4 py-2 border-b border-gray-200 dark:border-primary-600">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-orange-900 dark:text-orange-200">Stresstest-Ergebnisse</h3>
              <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                Simulation
              </span>
            </div>
          </div>
          <div className="p-4">
            {stressData ? (
              <div className="space-y-3">
                {/* Voltage display */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
                    <p className="text-xs text-gray-600 dark:text-primary-300">Min Spannung L-N</p>
                    <p className={`text-lg font-semibold ${
                      voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.10
                        ? 'text-red-600 dark:text-red-400'
                        : voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.05
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-green-600 dark:text-green-400'
                    }`}>
                      {voltageLnV !== undefined ? `${fmt(voltageLnV, 1)} V` : '–'}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
                    <p className="text-xs text-gray-600 dark:text-primary-300">Abweichung</p>
                    <p className={`text-lg font-semibold ${
                      voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.10
                        ? 'text-red-600 dark:text-red-400'
                        : voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.05
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-green-600 dark:text-green-400'
                    }`}>
                      {voltageLnPu !== undefined ? `${fmt(puDeviationPct(voltageLnPu), 1)} %` : '–'}
                    </p>
                  </div>
                </div>

                {/* Power and Current */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
                    <p className="text-xs text-gray-600 dark:text-primary-300">Leistung</p>
                    <p className={`text-lg font-semibold ${
                      stressData.powerKw < 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      {fmtOpt(stressData.powerKw, 2) ?? '–'} kW
                    </p>
                    {stressData.powerKw < 0 && (
                      <p className="text-[10px] text-yellow-600 dark:text-yellow-400">Einspeisung</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
                    <p className="text-xs text-gray-600 dark:text-primary-300">Strom</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {fmtOpt(stressData.currentA, 2) ?? '–'} A
                    </p>
                  </div>
                </div>

                {/* Voltage warning */}
                {voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.10 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Spannung außerhalb des zulässigen Bereichs (&gt;±10%)
                    </p>
                  </div>
                )}
                {voltageLnPu !== undefined && Math.abs(puDeviation(voltageLnPu)) > 0.05 && Math.abs(puDeviation(voltageLnPu)) <= 0.10 && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-2">
                    <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Spannungsabweichung erhöht (&gt;±5%)
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-primary-400 text-center py-2">
                Keine Ergebnisse für diesen Hausanschluss
              </p>
            )}
          </div>
        </div>
      )}

      {/* Technische Ressourcen Section - styled like other sections in HausanschlussDetails */}
      <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className={`px-4 py-2 border-b border-gray-200 dark:border-primary-600 ${
          hasResourceChanges
            ? 'bg-orange-50 dark:bg-orange-900/20'
            : 'bg-yellow-50 dark:bg-yellow-900/20'
        }`}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-primary-100">Technische Ressourcen</h3>
            {hasResourceChanges && (
              <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                Geändert
              </span>
            )}
          </div>
        </div>
        <div className="p-4 space-y-3">
          {mergedResources.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-primary-400 text-center py-2">
              Keine technischen Ressourcen
            </p>
          ) : (
            <div className="space-y-2">
              {mergedResources.map((resource) => {
                const isNew = override?.addedResources?.some(r => r.id === resource.id);
                const isModified = isResourceModified(resource.id);
                const effectivePower = getResourcePower(resource);

                const resourceActive = isResourceActive(resource.type);
                return (
                  <div
                    key={resource.id}
                    className={`p-3 rounded-lg border ${
                      !resourceActive
                        ? 'bg-red-50/50 dark:bg-red-900/10 border-red-300 dark:border-red-700'
                        : isNew
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                          : isModified
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
                            : 'bg-gray-50 dark:bg-primary-700 border-gray-200 dark:border-primary-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {resource.type === 'pv_anlage' && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isResourceActive(resource.type)
                              ? 'bg-yellow-100 dark:bg-yellow-900/40'
                              : 'bg-gray-200 dark:bg-gray-700'
                          }`}>
                            <svg className={`w-4 h-4 ${
                              isResourceActive(resource.type)
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-gray-400 dark:text-gray-500'
                            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                        )}
                        {resource.type === 'wallbox' && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isResourceActive(resource.type)
                              ? 'bg-blue-100 dark:bg-blue-900/40'
                              : 'bg-gray-200 dark:bg-gray-700'
                          }`}>
                            <svg className={`w-4 h-4 ${
                              isResourceActive(resource.type)
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-400 dark:text-gray-500'
                            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                        )}
                        {resource.type === 'heat_pump' && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isResourceActive(resource.type)
                              ? 'bg-red-100 dark:bg-red-900/40'
                              : 'bg-gray-200 dark:bg-gray-700'
                          }`}>
                            <svg className={`w-4 h-4 ${
                              isResourceActive(resource.type)
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-500'
                            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-primary-100">
                              {resource.type === 'pv_anlage' ? 'PV-Anlage' : resource.type === 'wallbox' ? 'Wallbox' : resource.type === 'heat_pump' ? 'Wärmepumpe' : resource.type}
                            </p>
                            {(resource.type === 'pv_anlage' || resource.type === 'wallbox' || resource.type === 'heat_pump') && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                isResourceActive(resource.type)
                                  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                                  : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                              }`}>
                                {isResourceActive(resource.type) ? 'Aktiv' : 'Deaktiviert'}
                              </span>
                            )}
                            {isNew && <span className="text-xs text-green-600 dark:text-green-400">(Neu)</span>}
                            {isModified && !isNew && <span className="text-xs text-orange-600 dark:text-orange-400">*</span>}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveResource(resource.id)}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        title="Entfernen"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    {/* Editable power input */}
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-500 dark:text-primary-400">Leistung:</label>
                      <input
                        type="number"
                        value={effectivePower}
                        onChange={(e) => handleResourcePowerChange(resource.id, Number(e.target.value))}
                        min={1}
                        className={`flex-1 px-2 py-1 text-sm border rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 ${
                          isModified && !isNew
                            ? 'border-orange-300 dark:border-orange-600 focus:ring-orange-500'
                            : 'border-gray-300 dark:border-primary-500 focus:ring-green-500'
                        }`}
                      />
                      <span className="text-xs text-gray-500 dark:text-primary-400">kW</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Resource Buttons - only show if resource type doesn't exist yet */}
          {(!hasPvAnlage || !hasWallbox || !hasHeatPump) && (
            <div className="grid grid-cols-3 gap-2">
              {!hasPvAnlage && (
                <button
                  onClick={() => { setShowAddPv(true); setShowAddWallbox(false); setShowAddWaermepumpe(false); }}
                  className={`py-2 px-2 text-xs border rounded-lg transition-colors flex flex-col items-center gap-1 ${
                    showAddPv
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400 text-yellow-700 dark:text-yellow-400'
                      : 'text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  PV-Anlage
                </button>
              )}
              {!hasWallbox && (
                <button
                  onClick={() => { setShowAddWallbox(true); setShowAddPv(false); setShowAddWaermepumpe(false); }}
                  className={`py-2 px-2 text-xs border rounded-lg transition-colors flex flex-col items-center gap-1 ${
                    showAddWallbox
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400 text-blue-700 dark:text-blue-400'
                      : 'text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Wallbox
                </button>
              )}
              {!hasHeatPump && (
                <button
                  onClick={() => { setShowAddWaermepumpe(true); setShowAddPv(false); setShowAddWallbox(false); }}
                  className={`py-2 px-2 text-xs border rounded-lg transition-colors flex flex-col items-center gap-1 ${
                    showAddWaermepumpe
                      ? 'bg-red-100 dark:bg-red-900/30 border-red-400 text-red-700 dark:text-red-400'
                      : 'text-red-700 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  </svg>
                  Wärmepumpe
                </button>
              )}
            </div>
          )}

          {/* Add PV Form */}
          {showAddPv && !hasPvAnlage && (
            <div className="border border-yellow-300 dark:border-yellow-600 rounded-lg p-3 space-y-3 bg-yellow-50 dark:bg-yellow-900/10">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 dark:text-primary-100">Neue PV-Anlage</h4>
                <button onClick={() => setShowAddPv(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div>
                <label className="text-xs text-gray-600 dark:text-primary-300">Maximale Leistung (kWp)</label>
                <input
                  type="number"
                  value={newPvPower}
                  onChange={(e) => setNewPvPower(Number(e.target.value))}
                  min={1}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                />
              </div>
              <button
                onClick={handleAddPvAnlage}
                className="w-full py-2 px-3 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg transition-colors"
              >
                Hinzufügen
              </button>
            </div>
          )}

          {/* Add Wallbox Form */}
          {showAddWallbox && !hasWallbox && (
            <div className="border border-blue-300 dark:border-blue-600 rounded-lg p-3 space-y-3 bg-blue-50 dark:bg-blue-900/10">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 dark:text-primary-100">Neue Wallbox</h4>
                <button onClick={() => setShowAddWallbox(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div>
                <label className="text-xs text-gray-600 dark:text-primary-300">Ladeleistung (kW)</label>
                <input
                  type="number"
                  value={newWallboxPower}
                  onChange={(e) => setNewWallboxPower(Number(e.target.value))}
                  min={1}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                />
              </div>
              <button
                onClick={handleAddWallbox}
                className="w-full py-2 px-3 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
              >
                Hinzufügen
              </button>
            </div>
          )}

          {/* Add Wärmepumpe Form */}
          {showAddWaermepumpe && !hasHeatPump && (
            <div className="border border-red-300 dark:border-red-600 rounded-lg p-3 space-y-3 bg-red-50 dark:bg-red-900/10">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 dark:text-primary-100">Neue Wärmepumpe</h4>
                <button onClick={() => setShowAddWaermepumpe(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div>
                <label className="text-xs text-gray-600 dark:text-primary-300">Elektrische Leistung (kW)</label>
                <input
                  type="number"
                  value={newWaermepumpePower}
                  onChange={(e) => setNewWaermepumpePower(Number(e.target.value))}
                  min={1}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                />
              </div>
              <button
                onClick={handleAddWaermepumpe}
                className="w-full py-2 px-3 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Hinzufügen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Steuerboxen Section (read-only) */}
      {originalElement.steuerboxes && originalElement.steuerboxes.length > 0 && (
        <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
          <div className="bg-gray-50 dark:bg-primary-700 px-4 py-2 border-b border-gray-200 dark:border-primary-600">
            <h3 className="text-sm font-medium text-gray-900 dark:text-primary-100">
              Steuerboxen ({originalElement.steuerboxes.length})
            </h3>
          </div>
          <div className="p-4 space-y-2">
            {originalElement.steuerboxes.map((box) => {
              const linkedResources = mergedResources.filter(
                tr => (box.linkedTechnicalResourceIds || []).includes(tr.id)
              );

              return (
                <div key={box.id} className="bg-gray-50 dark:bg-primary-700 rounded border border-gray-200 dark:border-primary-600 overflow-hidden">
                  <div className="flex items-center px-2 py-1.5">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${box.type === 'analog' ? 'bg-[#4285f4]' : 'bg-[#34a853]'}`} />
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] text-gray-900 dark:text-white truncate">
                          {box.name || (box.type === 'analog' ? 'FNN Steuerbox' : 'Energiemanager')}
                        </span>
                        <span className="text-[10px] text-gray-500 dark:text-primary-400">
                          {box.type === 'analog' ? `FNN (${box.numRelays || 4} Relais)` : 'Energiemanager'}
                          {linkedResources.length > 0 && ` · ${linkedResources.length} TR`}
                        </span>
                      </div>
                    </div>
                  </div>
                  {linkedResources.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-primary-600 px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {linkedResources.map(tr => {
                          const typeLabel = tr.type === 'pv_anlage' ? 'PV Anlage'
                            : tr.type === 'wallbox' ? 'Wallbox'
                            : tr.type === 'heat_pump' ? 'Wärmepumpe'
                            : tr.type === 'battery_storage' ? 'Batteriespeicher'
                            : tr.type;
                          const relayInfo = box.type === 'analog' && tr.relayNumber
                            ? ` (R${tr.relayNumber})`
                            : '';
                          return (
                            <span
                              key={tr.id}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-[10px] rounded"
                            >
                              {tr.name || typeLabel}{relayInfo} · {getResourcePower(tr)} kW
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reset Button */}
      {hasAnyChanges && (
        <button
          onClick={handleResetAllChanges}
          className="w-full py-2 px-3 text-sm text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-600 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Alle Szenario-Änderungen zurücksetzen
        </button>
      )}
    </div>
  );
};
